import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OrgsService } from "@app2/account/orgs.service";
import { UserService } from "@app2/account/user.service";
import { Domain } from "@app2/alert/constants";
import { RestClient, unwrapIfAppropriate } from "@app2/clients/rest-client";
import {
    Alert,
    AlertRelatedObjects,
    AlertResult,
    AlertStateChange,
    AnomalyAlertResult,
    DefaultTrigger,
    DefaultTriggerCopyStatus,
    DefaultTriggerGroup,
    DefaultTriggerGroupAnalysis,
    DefaultTriggerStats,
    DifferingTriggerFields,
    GetSuggestedTaskSchedulesResponse,
    InboxAlert,
    ItemViewerIds,
    LibraryTaskSchedule,
    LibraryTaskScheduleGroup,
    LibraryTaskScheduleOptions,
    MultipleAlertStateChangeRequest,
    QueryTrigger,
    QueryTriggerStats,
    RelatedAlertObjects,
    SequenceTriggerAlertResult,
    TaskSchedule,
    TaskScheduleDashboard,
    TriggerResyncInfo,
    TriggerResyncResponse,
    TriggerSchedule,
    TriggerType,
    UbaAlertResult,
} from "@app2/clients/types/alert-typedefs";
import { EnvironmentService } from "@app2/shared/services/environment.service";
import { TimeService } from "@app2/shared/services/time.service";
import { DEFAULT_PAGE_SIZE } from "@app2/shared/shared-constants";
import { FileAttachment, StartFileUploadResponse } from "@app2/shared/type-defs";
import { SlugName } from "@app2/ticket/ticket-constants";
import { databaseTriggerTypeEnumTypes } from "@app2/triggers/trigger-edit-page/constants";
import { Moment } from "moment";

export interface GetTriggersRequest {
    domain: Domain;
    lineItemIds?: uuid[];
    riskControlIds?: uuid[];
    tagIds?: uuid[];
}

export interface GetTaskSchedulesRequest {
    tagIds?: uuid[];
}

function correctTriggerType(trigger: Partial<QueryTrigger | DefaultTrigger>): Partial<QueryTrigger | DefaultTrigger> {
    return {
        ...trigger,
        type: databaseTriggerTypeEnumTypes.includes(trigger.type) ? trigger.type : TriggerType.custom,
    };
}

@Injectable({
    providedIn: "root",
})
export class AlertClientService {
    private alertClient: RestClient;
    private alertClientNoOrg: RestClient;

    constructor(httpClient: HttpClient,
                envService: EnvironmentService,
                orgsService: OrgsService,
                private timeService: TimeService,
                private userService: UserService) {
        const baseUrl = envService.getConfig().alertService;

        this.alertClient = new RestClient(baseUrl, httpClient, orgsService);
        this.alertClientNoOrg = new RestClient(baseUrl, httpClient);
    }

    // Alert Inbox

    public registerItemView(viewerId: uuid, registerItemId: uuid): Promise<void> {
        return this.alertClient.doPost(`/item-view/register`, null, {
            viewerId: viewerId, registerItemId: registerItemId,
        });
    }

    public getViewersForItems(cutoffSeconds: number, viewItemIds: uuid[]): Promise<ItemViewerIds> {
        return this.alertClient.doPost(`/item-view/viewers`, viewItemIds, {
            cutoffSeconds: cutoffSeconds,
        });
    }

    public registerAndGetViewersForItems(viewerId: uuid, registerItemId: uuid, cutoffSeconds: number, viewItemIds: uuid[]): Promise<ItemViewerIds> {
        return this.alertClient.doPost(`/item-view/register-and-viewers`, viewItemIds, {
            viewerId: viewerId, registerItemId: registerItemId, cutoffSeconds: cutoffSeconds,
        });
    }

    public getAlertPageGroupedByTrigger(sortDesc: boolean, sortField: string, domain: Domain,
                                        filters: string[], pageBoundary?: Alert,
                                        pageSize: number = DEFAULT_PAGE_SIZE): Promise<Alert[]> {
        return this.alertClient.doPost<{results: Alert[]}>(`/alerts/trigger-view`, pageBoundary,
            { pageSize, descending: sortDesc, sort: sortField, domain, filter: filters })
            .then(unwrapIfAppropriate);
    }

    public getAlertDetails(alertId: uuid): Promise<InboxAlert> {
        return this.alertClient.doGet(`/alert/${ alertId }`);
    }

    public getAlertDetailsList(alertIds: uuid[]): Promise<InboxAlert[]> {
        return this.alertClient.doPost(`/alert/alert-details`, alertIds);
    }

    public getSequenceAlertPage(pageSize: number, filters: string[],
                                pageBoundary?: SequenceTriggerAlertResult): Promise<SequenceTriggerAlertResult[]> {
        return this.alertClient.doPost(`/sequence-alerts`, pageBoundary,
            { pageSize, filter: filters })
            .then(unwrapIfAppropriate);
    }

    public getAlertPage(sortDesc: boolean, sortField: string, domain: Domain,
                        filters: string[], pageBoundary?: Alert, pageSize = DEFAULT_PAGE_SIZE): Promise<Alert[]> {
        return this.alertClient.doPost<{results: Alert[]}>(`/alerts`, pageBoundary,
            { pageSize: pageSize, descending: sortDesc, sort: sortField, domain, filter: filters })
            .then(unwrapIfAppropriate);
    }

    public getAnomalyAlertPage(pageSize: number, filters: string[], pageBoundary?: AnomalyAlertResult): Promise<AnomalyAlertResult[]> {
        return this.alertClient.doPost("/anomaly-alerts", pageBoundary, { pageSize, filter: filters })
            .then(unwrapIfAppropriate)
            .then((anomalies: AnomalyAlertResult[]) => anomalies.map(anomaly => {
                // range for non high priorty terms is 24 hours
                // range for high priority terms is 4 hours
                // logic found in rML/modelspike/modelspike/model_zero_shot.py in _set_period, where range is 24 / period.

                const anomalyFoundMoment = this.timeService.timeZoneMoment(anomaly.anomalyTimestamp);
                const anomalyEndMoment = anomaly.primaryTerm
                && (anomaly.isHighPriorityCountry || anomaly.isHighPriorityPort)
                        ? anomalyFoundMoment.clone().add(4, "Hours")
                        : anomalyFoundMoment.clone().add(24, "Hours");
                return {
                    ...anomaly,
                    anomalyEndTimestamp: anomalyEndMoment.toISOString(),
                };
            }));
    }

    public getUbaAlertPage(pageSize: number, filters: string[], pageBoundary?: UbaAlertResult): Promise<UbaAlertResult[]> {
        return this.alertClient.doPost("/uba-alerts", pageBoundary, { pageSize, filter: filters });
    }

    public getAlertsForAggregateValue(primaryTerm: string, pageSize?: number): Promise<InboxAlert[]> {
        return this.alertClient.doPost(`/alerts/aggregate-value`, null,
            { primaryTerm: primaryTerm, pageSize: pageSize ?? 100});
    }

    public getTopAggregationsForOrg(): Promise<string[]> {
        return this.alertClient.doGet(`/top-aggregations`);
    }

    public escalateNewAndAckedAlerts(forValue: string,
                                     multipleAlertsStateChange: MultipleAlertStateChangeRequest): Promise<uuid[]> {
        return this.alertClient.doPost(`/alerts/multiple/state/escalate`, multipleAlertsStateChange,
            {forValue: forValue});
    }

    public changeAlertStateForTrigger(triggerId: uuid, beforeThis: string,
                                      stateChange: AlertStateChange): Promise<uuid[]> {
        return this.alertClient.doPost(`/alert/trigger/${triggerId}/state`, stateChange,
            {
                beforeThis,
            })
            .then(unwrapIfAppropriate);
    }

    public getAlertIdsForTriggerAndState(triggerId: uuid, state: string, beforeThis: string): Promise<uuid[]> {
        return this.alertClient.doGet(`/alert/trigger/${ triggerId }/state`,
            { state, beforeThis })
            .then(unwrapIfAppropriate);
    }

    public getTriggerStats(triggerId: uuid, start: date, end: date, primaryTerm?: string): Promise<AlertResult> {
        return this.alertClient.doGet(`/trigger/${ triggerId }/stats/${ start }/to/${ end }`,
            { primaryTerm });
    }

    public getFiredAlertStateCounts(begin: date, end: date): Promise<Record<string, number>> {
        return this.alertClient.doGet(`/report/alert/state`, { begin, end });
    }

    // Triggers

    public getTrigger(triggerId: uuid): Promise<QueryTrigger> {
        return this.alertClient.doGet(`/trigger/${triggerId}`);
    }

    public getTriggers(includeDeleted: boolean, request: GetTriggersRequest): Promise<QueryTrigger[]> {
        return this.alertClient.doPost(`/triggers`, request, { includeDeleted });
    }

    public downloadTriggersCsv(triggerIds: uuid[], domain: Domain): Promise<Blob> {
        return this.alertClient.downloadCsvPost("/triggers-csv", {triggerIds, domain});
    }

    public getQueryTriggerStats(triggerId: uuid, begin: Moment, end: Moment): Promise<QueryTriggerStats> {
        const params = {
            begin: begin.toISOString(),
            end: end.toISOString(),
        };
        return this.alertClient.doGet(`/trigger/${ triggerId }/stats`, params);
    }

    public createTrigger(trigger: Partial<QueryTrigger>, defaultTriggerId?: uuid): Promise<QueryTrigger> {
        return this.alertClient.doPost(`/trigger`, correctTriggerType(trigger), {
            defaultTriggerId,
        });
    }

    public pauseTrigger(triggerId: uuid, pauseSeconds?: number): Promise<QueryTrigger> {
        return this.alertClient.doPost(`/trigger/${ triggerId }/pause`, undefined, { pauseSeconds });
    }

    public updateTrigger(trigger: QueryTrigger, archiveConfirmation = false): Promise<QueryTrigger> {
        return this.alertClient.doPut(`/trigger/${ trigger.id }`, correctTriggerType(trigger), { archiveConfirmation });
    }

    // Library Triggers

    public getDefaultTrigger(triggerId: uuid): Promise<DefaultTrigger> {
        return this.alertClient.doGet(`/trigger/default/trigger/${triggerId}`, { includeDeleted: true });
    }

    public getDefaultTriggerGroups(): Promise<DefaultTriggerGroup[]> {
        return this.alertClient.doGet("/trigger/default/group");
    }

    public createDefaultTrigger(groupId: uuid, trigger: DefaultTrigger): Promise<DefaultTrigger> {
        return this.alertClientNoOrg.doPost(`/org/all/trigger/default/group/${ groupId }`,
            correctTriggerType(trigger));
    }

    public updateDefaultTrigger(groupId: uuid, trigger: DefaultTrigger): Promise<DefaultTrigger> {
        return this.alertClientNoOrg.doPut(`/org/all/trigger/default/group/${ groupId }/trigger/${ trigger.id }`,
            correctTriggerType(trigger));
    }

    public createDefaultTriggerGroup(triggerGroup: DefaultTriggerGroup): Promise<DefaultTrigger> {
        return this.alertClientNoOrg.doPost(`/org/all/trigger/default/group`, triggerGroup);
    }

    public updateDefaultTriggerGroup(triggerGroup: DefaultTriggerGroup): Promise<DefaultTrigger> {
        return this.alertClientNoOrg.doPut(`/org/all/trigger/default/group/${ triggerGroup.id }`, triggerGroup);
    }

    public deleteDefaultTrigger(groupId: uuid, triggerId: uuid): Promise<void> {
        return this.alertClientNoOrg.doDelete(`/org/all/trigger/default/group/${ groupId }/trigger/${ triggerId }`);
    }

    public getDefaultTriggerStats(groupId: uuid, triggerId: uuid, begin: Moment, end: Moment): Promise<DefaultTriggerStats[]> {
        const params = {
            begin: begin.toISOString(),
            end: end.toISOString(),
        };
        return this.alertClientNoOrg.doGet(`/org/all/trigger/default/group/${ groupId }/trigger/${ triggerId }/stats`, params);
    }

    public deleteDefaultTriggerGroup(groupId: uuid): Promise<void> {
        return this.alertClientNoOrg.doDelete(`/org/all/trigger/default/group/${ groupId }`);
    }

    public resumeTrigger(triggerId: uuid): Promise<QueryTrigger> {
        return this.alertClient.doPost((`/trigger/${triggerId}/resume`));
    }


    public pauseNoDataTriggers(tagName: string, pauseSeconds: number) {
        return this.alertClientNoOrg.doPost(`/org/trigger/pauseTriggersByTag`, undefined, { tagName, pauseSeconds });
    }

    public resumeNoDataTriggers(tagName: string) {
        return this.alertClientNoOrg.doPost(`/org/trigger/resumeTriggersByTag`, undefined, { tagName });
    }

    // Task Schedules

    public createTaskSchedule(taskSchedule: TaskSchedule): Promise<TaskSchedule> {
        return this.alertClient.doPost("/task-schedule", taskSchedule);
    }

    public getTaskSchedule(taskScheduleId: uuid): Promise<TaskSchedule> {
        return this.alertClient.doGet(`/task-schedule/${taskScheduleId}`);
    }

    public getTaskSchedules(includeDeleted = false): Promise<TaskSchedule[]> {
        return this.alertClient.doGet("/task-schedules", { includeDeleted });
    }

    public getFilteredTaskSchedules(request: GetTaskSchedulesRequest): Promise<TaskSchedule[]> {
        return this.alertClient.doPost("/task-schedules", request);
    }

    public downloadTaskScheduleCsv(): Promise<Blob> {
        return this.alertClient.downloadCsvGet("/task-schedule/export");
    }

    public deleteTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doDelete(`/task-schedule/${taskScheduleId}`);
    }

    public editTaskSchedule(taskSchedule: TaskSchedule, updateNextRun = false): Promise<TaskSchedule> {
        return this.alertClient.doPost(`/task-schedule/${taskSchedule.id}`, taskSchedule, { updateNextRun });
    }

    public restoreTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doPost(`/task-schedule/${taskScheduleId}/restore`);
    }

    public pauseTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doPost(`/task-schedule/${taskScheduleId}/pause`);
    }

    public resumeTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doPost(`/task-schedule/${taskScheduleId}/resume`);
    }

    public requestTaskScheduleToRunImmediately(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doPost(`/task-schedule/${ taskScheduleId }/run-immediately`);
    }

    public getLibraryTaskSchedules(includeDeleted = false): Promise<LibraryTaskSchedule[]> {
        return this.alertClientNoOrg.doGet("/library-task-schedules", { includeDeleted });
    }

    public getLibraryTaskSchedule(libraryTaskScheduleId: uuid): Promise<LibraryTaskSchedule> {
        return this.alertClientNoOrg.doGet(`/library-task-schedule/${ libraryTaskScheduleId }`);
    }

    public createLibraryTaskSchedule(schedule: LibraryTaskSchedule): Promise<LibraryTaskSchedule> {
        return this.alertClientNoOrg.doPost("/library-task-schedule", schedule);
    }

    public editLibraryTaskSchedule(schedule: LibraryTaskSchedule): Promise<LibraryTaskSchedule> {
        return this.alertClientNoOrg.doPost(`/library-task-schedule/${ schedule.id }`, schedule);
    }

    public editLibraryTaskScheduleRiskLinks(scheduleId: uuid, controlIds: uuid[]): Promise<void> {
        return this.alertClientNoOrg.doPut(`/org/all/library-task-schedule/${ scheduleId }/link-risks`,
            { controlIds });
    }

    public getLibraryTaskScheduleGroups(includeDeleted = false): Promise<LibraryTaskScheduleGroup[]> {
        return this.alertClient.doGet("/library-task-schedule/groups", { includeDeleted });
    }

    public createLibraryTaskScheduleGroup(group: LibraryTaskScheduleGroup): Promise<void> {
        return this.alertClientNoOrg.doPost("/library-task-schedule/group", group);
    }

    public deleteLibraryTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClientNoOrg.doDelete(`/library-task-schedule/${taskScheduleId}`);
    }

    public copyLibraryTaskSchedulesToAuthedOrgs(libraryTaskScheduleOptions: LibraryTaskScheduleOptions, orgIds: uuid[]): Promise<uuid[]> {
        return this.alertClientNoOrg.doPost(`/library-task-schedule/copy-to-accounts/`, libraryTaskScheduleOptions, { orgId: orgIds });
    }

    public copyLibraryTaskSchedulesToOrg(libraryTaskScheduleOptions: LibraryTaskScheduleOptions): Promise<uuid[]> {
        return this.alertClient.doPost("/library-task-schedule/copy", libraryTaskScheduleOptions);
    }

    public restoreLibraryTaskSchedule(taskScheduleId: uuid): Promise<void> {
        return this.alertClientNoOrg.doPost(`/library-task-schedule/${ taskScheduleId }/restore`);
    }

    public getTaskScheduleSuggestionsForControl(riskControlId: uuid, libRiskControlId: uuid): Promise<GetSuggestedTaskSchedulesResponse> {
        return this.alertClient.doGet(`/library-task-schedules/suggestions`,
            { riskControlId, libRiskControlId });
    }

    public getSavedQueryDependents(savedQueryIds: uuid[]): Promise<AlertRelatedObjects> {
        return this.alertClient.doPost("/referenced-by-saved-query", savedQueryIds);
    }

    public getAllSavedQueryDependents(savedQueryIds: uuid[] | null): Promise<{ [key: string]: RelatedAlertObjects }> {
        return this.alertClient.doPost("/referenced-by-saved-query-by-id", savedQueryIds);
    }

    public getLabels(): Promise<string[]> {
        return this.alertClient.doGet("/labels");
    }

    public deleteWatchList(id: uuid): Promise<void> {
        return this.alertClient.doDelete(`/watchlist/${id}`);
    }

    public getTaskScheduleFiles(taskScheduleId: uuid): Promise<FileAttachment[]> {
        return this.alertClient.doGet(`/task-schedule/${ taskScheduleId }/files`);
    }

    public startTaskScheduleFileUpload(taskScheduleId: uuid, file: FileAttachment, contentType: string): Promise<StartFileUploadResponse> {
        return this.alertClient.doPost(`/task-schedule/${ taskScheduleId }/file`, file, { contentType });
    }

    public finishTaskScheduleFileUpload(taskScheduleId: uuid, fileId: uuid): Promise<void> {
        return this.alertClient.doPost(`/task-schedule/${ taskScheduleId }/file/${ fileId }`, null);
    }

    public deleteTaskScheduleFile(taskScheduleId: uuid, fileId: uuid): Promise<void> {
        return this.alertClient.doDelete(`/task-schedule/${ taskScheduleId }/file/${ fileId }`);
    }

    public getDownloadTaskScheduleFileUrl(taskScheduleId: uuid, fileId: uuid): string {
        return this.alertClient.getFullUrlFor(`/task-schedule/${ taskScheduleId }/file/${ fileId }`);
    }

    // Task Schedule Dashboard

    public getTaskScheduleDashboard(taskScheduleId: uuid): Promise<TaskScheduleDashboard> {
        return this.alertClient.doGet(`/task-schedule/${ taskScheduleId }/dashboard`);
    }

    public attachTaskScheduleDashboard(taskScheduleId: uuid, dashboard: TaskScheduleDashboard): Promise<TaskScheduleDashboard> {
        return this.alertClient.doPut(`/task-schedule/${ taskScheduleId }/dashboard`, dashboard);
    }

    public detachTaskScheduleDashboard(taskScheduleId: uuid): Promise<void> {
        return this.alertClient.doDelete(`/task-schedule/${ taskScheduleId }/dashboard`);
    }

    public buildEmptyTaskSchedule(): Partial<TaskSchedule> {
        const currentUser = this.userService.getCurrentUser();
        return {
            name: "",
            ticketOwner: currentUser!.id,
            description: "",
            queries: [],
            nextRun: this.timeService.now().format(this.timeService.formats.timestampDate),
            scheduleUnit: "week",
            scheduleInterval: 1,
            timeZoneId: this.timeService.getTimezone(),
            labels: [],
            lineItemIds: [],
            severity: "NONE",
            watchers: [],
            dueTime: undefined,
            dueInUnit: undefined,
            dueInInterval: undefined,
        };
    }

    public buildEmptyTriggerSchedule(): Partial<TriggerSchedule> {
        return {
            id: null,
            name: "",
            description: "",
            timeZoneId: this.timeService.getTimezone(),
            intervals: [],
        };
    }

    public triggerNeedsArchive(oldTrigger: QueryTrigger, newTrigger: QueryTrigger): boolean {
        return !!oldTrigger && !!newTrigger &&
            (oldTrigger.type !== newTrigger.type ||
                oldTrigger.intervalSeconds !== newTrigger.intervalSeconds);
    }

    public resetCardinality(triggerId: uuid): Promise<QueryTrigger> {
        return this.alertClient.doPost(`/trigger/${triggerId}/reset-cardinality`);
    }

    public deleteTrigger(triggerId: uuid): Promise<void> {
        return this.alertClient.doDelete(`/trigger/${triggerId}`);
    }

    public restoreTrigger(triggerId: uuid): Promise<void> {
        return this.alertClient.doPost(`/trigger/${triggerId}/undelete`);
    }

    public changeAlertState(alertId, stateChange: AlertStateChange): Promise<Alert> {
        return this.alertClient.doPost(`/alert/${alertId}/state`, stateChange);
    }

    /**
     * Trigger Schedules
     */
    public createTriggerSchedule(schedule: TriggerSchedule): Promise<TriggerSchedule> {
        return this.alertClient.doPost("/trigger-schedule", schedule);
    }

    public editTriggerSchedule(schedule: TriggerSchedule): Promise<TriggerSchedule> {
        return this.alertClient.doPost(`/trigger-schedule/${ schedule.id }`, schedule);
    }

    public getTriggerSchedules(includeDeleted: boolean): Promise<TriggerSchedule[]> {
        return this.alertClient.doGet("/trigger-schedules", { includeDeleted });
    }

    public getTriggerSchedule(scheduleId: uuid): Promise<TriggerSchedule> {
        return this.alertClient.doGet(`/trigger-schedule/${ scheduleId }`);
    }

    public getTriggersWithSchedule(scheduleId: uuid): Promise<QueryTrigger[]> {
        return this.alertClient.doGet(`/trigger-schedule/${ scheduleId }/triggers`);
    }

    public deleteTriggerSchedule(scheduleId: uuid): Promise<void> {
        return this.alertClient.doDelete(`/trigger-schedule/${ scheduleId }`);
    }

    public bulkDeleteTriggerSchedules(scheduleIds: uuid[]): Promise<uuid[]> {
        return this.alertClient.doDelete(`/trigger-schedules`, { triggerScheduleId: scheduleIds });
    }

    public getAlertPageForTicket(slugName: SlugName, slugId: number): Promise<Alert[]> {
        const params = {
            pageSize: 20,
            ticketSlugName: slugName,
            ticketSlugId: slugId,
        };
        return this.alertClient.doPost(`/alerts/ticket`, null, params)
            .then(unwrapIfAppropriate);
    }

    public undeleteTrigger(triggerId: uuid): Promise<void> {
        return this.alertClient.doPost(`/trigger/${ triggerId }/undelete`);
    }

    // Trigger Re-sync
    public calculateTriggerResyncInfo(triggerIds: uuid[]): Promise<Record<uuid, TriggerResyncInfo>> {
        return this.alertClient.doPost(`/trigger/calculate-resync-info`, triggerIds);
    }

    public resyncTrigger(triggerId: uuid, fieldsToUpdate: DifferingTriggerFields): Promise<TriggerResyncResponse> {
        return this.alertClient.doPost(`/trigger/${ triggerId }/resync`, fieldsToUpdate);
    }

    //  Default trigger group analysis
    public getDefaultTriggerGroupAnalyses(defaultTriggerGroupIds: uuid[]): Promise<Record<uuid, DefaultTriggerGroupAnalysis>> {
        return this.alertClient.doGet(`/default-trigger-group-analysis`,
            { defaultTriggerGroupIds: defaultTriggerGroupIds });
    }

    public requestDefaultTriggerGroupAnalyses(defaultTriggerGroupIds: uuid[]) {
        return this.alertClient.doPost(`/default-trigger-group-analysis`, null,
            { defaultTriggerGroupIds: defaultTriggerGroupIds });
    }

    // Bulk enabling/disabling triggers
    public bulkPauseTrigger(defaultTriggerId: uuid, orgIds: uuid[]) {
        return this.alertClientNoOrg.doPost(`/default-trigger/${ defaultTriggerId }/bulk-pause-primary-copies`, orgIds);
    }

    public getStatusesForDefaultTriggerCopies(defaultTriggerId: uuid, orgIds: uuid[]): Promise<Record<uuid, DefaultTriggerCopyStatus>> {
        return this.alertClientNoOrg.doPost(`/default-trigger/${ defaultTriggerId }/primary-copy-statuses`, orgIds);
    }

    public bulkActivateTrigger(defaultTriggerId: uuid, orgIds: uuid[]) {
        return this.alertClientNoOrg.doPost(`/default-trigger/${ defaultTriggerId }/bulk-activate-primary-copies`, orgIds);
    }
}
